import { TextField, TextFieldProps } from "@fxc/ui-kit";
import {
  Control,
  Controller,
  ControllerProps,
  FieldError,
  FieldValues,
  Path,
} from "react-hook-form";

export type TextFieldElementProps<T extends FieldValues = FieldValues> = Omit<
  TextFieldProps,
  "name"
> & {
  validation?: ControllerProps["rules"];
  /** Same as `validation` — alias for react-hook-form-mui `TextFieldElement` API */
  rules?: ControllerProps["rules"];
  name: Path<T>;
  parseError?: (error: FieldError) => string;
  control?: Control<T>;
  trimWhitespaces?: boolean; // New prop to trim whitespaces
};

export default function TextFieldElement<
  TFieldValues extends FieldValues = FieldValues,
>({
  validation: validationProp,
  rules,
  parseError,
  type,
  required,
  name,
  control,
  trimWhitespaces = false,
  ...rest
}: TextFieldElementProps<TFieldValues>): JSX.Element {
  const validation = { ...(rules ?? {}), ...(validationProp ?? {}) };

  if (required && !validation.required) {
    validation.required = "This field is required";
  }

  if (type === "email" && !validation.pattern) {
    validation.pattern = {
      value:
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      message: "Please enter a valid email address",
    };
  }

  return (
    <Controller
      name={name}
      control={control as Control<FieldValues>}
      rules={validation}
      render={({
        field: { value, onChange, onBlur, ref },
        fieldState: { invalid, error },
      }) => {
        const handleTrim = (newValue: string) => {
          if (trimWhitespaces) {
            newValue = newValue.trim();
            if (newValue !== value) {
              onChange(newValue);
            }
          }
        };

        return (
          <TextField
            data-qa={name}
            {...rest}
            inputRef={ref}
            name={name}
            value={value ?? ""}
            onChange={(ev) => {
              onChange(ev);
              if (typeof rest.onChange === "function") {
                rest.onChange(ev);
              }
            }}
            onBlur={(ev) => {
              handleTrim(ev.target.value);
              onBlur();
              if (typeof rest.onBlur === "function") {
                rest.onBlur(ev);
              }
            }}
            onKeyDown={(ev) => {
              if (ev.key === "Enter") {
                handleTrim(value);
              }
              if (typeof rest.onKeyDown === "function") {
                rest.onKeyDown(ev);
              }
            }}
            required={required}
            type={type}
            error={invalid}
            helperText={
              error
                ? typeof parseError === "function"
                  ? parseError(error)
                  : error.message
                : rest.helperText
            }
          />
        );
      }}
    />
  );
}
